import { FormField, InputAdornment, InputSlider, LinkButton, UIHeading2, UIHeading3, UIText2 } from '@cian/ui-kit';
import { plural } from '@cian/utils';
import cx from 'clsx';
import * as React from 'react';

import { IBank } from 'shared/types/banksList';
import { pluralize } from 'shared/utils/pluralize';

import * as styles from './styles.css';
import { BanksList } from '../../../../mortgage-assets';
import { calcPayment, formatNumber } from '../../helpers';

interface IMortgageCalculatorProps {
  initialPrice: number;
  initialRate: number;
  initialYears: number;
  initialPaymentPercent: number;
  banksList: IBank[];
  onLogoClick?(): void;
  onSubmitButtonClick?(): void;
  onDetailsButtonClick?(): void;
}

export function MortgageCalculator(props: IMortgageCalculatorProps) {
  const { initialPrice, initialRate, initialYears } = props;
  const [price, setPrice] = React.useState(initialPrice);
  const [initialPayment, setInitialPayment] = React.useState(price * (props.initialPaymentPercent / 100));
  const [rate, setRate] = React.useState(initialRate);
  const [years, setYears] = React.useState(initialYears);

  const initialPaymentPercent = Math.min(100, Math.floor((initialPayment / price) * 100));
  const payment = calcPayment(price, initialPayment, rate, years * 12);
  const requiredIncome = payment / 0.6;

  const { minPrice, maxPrice } = React.useMemo(() => {
    const minPrice = Math.min(1e5, initialPrice);
    const maxPrice = Math.max(5e7, initialPrice);

    return {
      minPrice,
      maxPrice,
    };
  }, [initialPrice]);

  return (
    <div className={styles['container']}>
      <a
        href={makeMortgageLink('logo')}
        // eslint-disable-next-line react/jsx-no-target-blank
        target="_blank"
        rel="noopener noreferrer"
        onClick={props.onLogoClick}
        className={styles['logo']}
      >
        <div className={styles['icon']} />
        <div>
          <span className={cx(styles['title'], styles['bold'])}>циан</span>
          <span className={cx(styles['title'], styles['dot'])}>.</span>
          <span className={styles['title']}>ипотека</span>
        </div>
      </a>
      <div className={styles['fields']}>
        <div className={styles['field']}>
          <FormField label="Стоимость недвижимости" withoutMargin>
            <InputSlider
              value={price}
              onChange={setPrice}
              rightAdornment={<InputAdornment>₽</InputAdornment>}
              min={minPrice}
              max={maxPrice}
              step={100000}
            />
          </FormField>
        </div>
        <div className={styles['field']}>
          <div className={styles['percent']}>
            <UIText2 as="span" color="black_60">
              {initialPaymentPercent}%
            </UIText2>
          </div>
          <FormField label="Первоначальный взнос" withoutMargin>
            <InputSlider
              value={initialPayment}
              onChange={setInitialPayment}
              rightAdornment={<InputAdornment>₽</InputAdornment>}
              min={0}
              max={price}
              step={100000}
            />
          </FormField>
        </div>
        <div className={styles['field']}>
          <FormField label="Ставка" withoutMargin>
            <InputSlider
              value={rate}
              onChange={setRate}
              rightAdornment={<InputAdornment>%</InputAdornment>}
              min={0}
              max={20}
              step={0.1}
            />
          </FormField>
        </div>
        <div className={styles['field']}>
          <FormField label="Срок" withoutMargin>
            <InputSlider
              value={years}
              onChange={setYears}
              rightAdornment={<InputAdornment>{plural(years, ['год', 'года', 'лет'])}</InputAdornment>}
              min={1}
              max={30}
              step={1}
            />
          </FormField>
        </div>
        <div className={cx(styles['field'], styles['result'], styles['strong'])}>
          <FormField label="Ежемесячный платеж" withoutMargin>
            <UIHeading2 as="span">{formatNumber(payment)} ₽</UIHeading2>
          </FormField>
        </div>
        <div className={cx(styles['field'], styles['result'])}>
          <FormField label="Необходимый доход" withoutMargin>
            <UIHeading2 as="span">{formatNumber(requiredIncome)} ₽</UIHeading2>
          </FormField>
        </div>
      </div>
      <UIHeading3>Один запрос в {pluralizeToBanks(props.banksList.length)}</UIHeading3>
      <div className={styles['banks']}>
        <BanksList banksList={props.banksList} />
      </div>
      <div className={styles['buttons']}>
        <LinkButton
          href={makeMortgageLink('online')}
          target="_blank"
          rel="noreferrer noopener"
          theme="fill_primary"
          size="M"
          onClick={props.onSubmitButtonClick}
        >
          Подать заявку онлайн
        </LinkButton>
        <LinkButton
          href={makeMortgageLink('details')}
          target="_blank"
          rel="noreferrer noopener"
          theme="fill_white_primary"
          size="M"
          onClick={props.onDetailsButtonClick}
        >
          Узнать подробнее
        </LinkButton>
      </div>
    </div>
  );
}

function makeMortgageLink(place: string) {
  return `https://cian.ru/ipoteka-main/?cian_source=cian&cian_medium=trap&cian_campaign=calc&cian_content=vipotekucalculator_${place}&cian_term=card`;
}

const pluralToBanksForms: [string, string, string] = ['банк', 'банка', 'банков'];

function pluralizeToBanks(count: number) {
  return pluralize(count, pluralToBanksForms);
}
